import React, { useState, useRef, useEffect, useLayoutEffect, forwardRef, useImperativeHandle, Ref, useMemo } from 'react';
import { Stage, Layer, Rect } from 'react-konva';
import { v4 as uuid } from 'uuid';
import { Annotation } from './Annotation';
import { ImageContainer } from './ImageContainer';
import { Annotation as AnnotationType, Dimension } from '../annotationTypes';
import { useZoomNativeConfig } from '../../../hoc/withZoomViewer';

import {
  // getCloseButtonPositionOnLoad,
  // convertToYolovObb8,
  prepareJSONData,
  fileDownload,
  getImageDownloadExportOptions,
} from '../annotationUtils';

interface UserType {
  USER_ID: string | number;
  USER_ROLES: string[];
}

interface DashboardProps {
  image1: string;
  imageFrame: any;
  cameraId: string;
  downloadFileName?: string;
  annotations: AnnotationType[];
  imagesType: string;
  setAnnotations: React.Dispatch<React.SetStateAction<AnnotationType[]>>;
  selectedId: string;
  selectAnnotation: (id: string) => void;
  onStartAnnotationCreate?: (startCreate: () => void) => void;
  isAddAnnotation: boolean;
  handleAddAnnotations: (val: boolean) => void;
  handleSubmitAnnotations1: () => void;
  handleUpoadDamages: (json: string, imageId: string, blob: Blob, stageWidth: number, stageHeight: number) => void;
  user: UserType;
  disableAnnotation?: boolean;
}

export interface DashboardRef {
  triggerSaveMethod: () => Promise<void>;
  triggerSubmitMethod: () => void;
  triggerClearMethod: () => void;
  triggerDamageMethod: (value: string) => void;
}

const isCanvasBackgroundTarget = (target: { getClassName: () => string; getStage: () => unknown }) => {
  const className = target.getClassName();
  return className === 'Image' || className === 'Stage' || className === 'Rect';
};

const isCanvasCoordinateAnnotation = (annotation: AnnotationType) => {
  if (annotation.coordinateSpace === 'canvas') {
    return true;
  }

  if (annotation.coordinateSpace === 'image') {
    return false;
  }

  // Fallback for annotations that predate the explicit flag: a new in-progress
  // draw or a locally drag/resize-modified annotation is always canvas-space.
  // Anything else (fresh, untouched server data) is real-image-pixel-space.
  return String(annotation?.id ?? '').includes('new_') || Boolean(annotation?.action);
};

const scaleAnnotationForCanvas = (annotation: AnnotationType, canvasMeasures: Dimension, imageMeasures: Dimension): AnnotationType => {
  const sourceWidth = annotation.imageWidth || imageMeasures.width;
  const sourceHeight = annotation.imageHeight || imageMeasures.height;

  if (!sourceWidth || !sourceHeight || isCanvasCoordinateAnnotation(annotation)) {
    return {
      ...annotation,
      frameWidth: canvasMeasures.width,
      frameHeight: canvasMeasures.height,
      coordinateSpace: 'canvas',
    };
  }

  const scaleX = canvasMeasures.width / sourceWidth;
  const scaleY = canvasMeasures.height / sourceHeight;

  return {
    ...annotation,
    x: annotation.x * scaleX,
    y: annotation.y * scaleY,
    x1: annotation.x1 * scaleX,
    y1: annotation.y1 * scaleY,
    x2: annotation.x2 * scaleX,
    y2: annotation.y2 * scaleY,
    width: annotation.width * scaleX,
    height: annotation.height * scaleY,
    imageWidth: sourceWidth,
    imageHeight: sourceHeight,
    frameWidth: canvasMeasures.width,
    frameHeight: canvasMeasures.height,
    coordinateSpace: 'canvas',
  };
};

/* ===========================
   COMPONENT
=========================== */

export const Dashboard = forwardRef((props: DashboardProps, ref: Ref<DashboardRef>) => {
  const {
    image1,
    imageFrame,
    cameraId,
    downloadFileName,
    annotations,
    setAnnotations,
    selectedId,
    selectAnnotation,
    onStartAnnotationCreate,
    isAddAnnotation,
    handleUpoadDamages,
    user,
    disableAnnotation = false,
  } = props;
  const stageRef = useRef<any>(null);
  const imageRef = useRef<HTMLImageElement | null>(null);
  const { setNativeZoom } = useZoomNativeConfig();

  const [newAnnotation, setNewAnnotation] = useState<AnnotationType[]>([]);
  const [, setSelectedDamageType] = useState<string>('00000');

  const [canvasMeasures, setCanvasMeasures] = useState<Dimension>({
    width: 0,
    height: 0,
  });

  const [imageMeasures, setImageMeasures] = useState<Dimension>({
    width: 0,
    height: 0,
  });

  const [images, setImages] = useState([{ id: cameraId, url: image1 }]);
  const [selectedImage] = useState<number>(0);
  const [allData, setAllData] = useState<Record<string, AnnotationType[]>>({});
  // The image failing to load shouldn't leave annotations interactive over a
  // blank canvas - drawing/dragging against an image that never loaded would
  // save coordinates against nothing.
  const [imageLoadError, setImageLoadError] = useState(false);
  const canInteractWithAnnotations = !disableAnnotation && isAddAnnotation && !imageLoadError;

  // image1/cameraId can change while this component stays mounted (e.g. the
  // representative image of a defect group changes after a save). Without
  // this, `images` stays frozen at whatever it was on first mount and the
  // canvas keeps rendering the old image while annotations move on.
  useEffect(() => {
    setImages([{ id: cameraId, url: image1 }]);
  }, [image1, cameraId]);

  const getCompositeDataURL = (mimeType?: string, quality?: number, pixelRatio = 1) => {
    const stage = stageRef.current;
    const imageNode = imageRef.current;
    if (!stage) {
      return '';
    }

    const width = Math.max(1, Math.round((stage.width() || stageWidth) * pixelRatio));
    const height = Math.max(1, Math.round((stage.height() || stageHeight) * pixelRatio));
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext('2d');

    if (!context) {
      return stage.toDataURL({ mimeType, quality, pixelRatio });
    }

    context.imageSmoothingEnabled = true;
    context.imageSmoothingQuality = 'high';

    if (imageNode) {
      context.drawImage(imageNode, 0, 0, width, height);
    }

    const overlay = stage.toCanvas({ pixelRatio });
    context.drawImage(overlay, 0, 0);

    return canvas.toDataURL(mimeType, quality);
  };

  const handleSaveAnnots = () => {
    // save annotations for curernt image in allData object
    const data = { ...allData };
    console.log('annotationsToDraw : ', annotationsToDraw);
    data[images[selectedImage].id] = annotationsToDraw;
    setAllData(data);
  };
  /* ===========================
       IMPERATIVE METHODS
    =========================== */

  useImperativeHandle(ref, () => ({
    triggerSaveMethod: async () => {
      handleSaveAnnots();
      if (!stageRef.current) return;

      const dataURL = getCompositeDataURL('image/png', undefined, 1);
      const blob = await fetch(dataURL).then(res => res.blob());
      console.log(allData, canvasMeasures.width, canvasMeasures.height, imageMeasures.width, imageMeasures.height);
      const json = JSON.stringify(
        prepareJSONData(allData, canvasMeasures.width, canvasMeasures.height, imageMeasures.width, imageMeasures.height)
      );

      handleUpoadDamages(json.replace(/\s+/g, ''), images[selectedImage].id, blob, stageRef.current.width(), stageRef.current.height());
    },

    triggerSubmitMethod: () => {
      if (!stageRef.current) return;

      const resolvedFileName = downloadFileName || `${cameraId}`;
      const exportOptions = getImageDownloadExportOptions(resolvedFileName || image1);
      // Export at native image resolution (not the on-screen canvas size) and
      // keep the source format (JPEG stays JPEG) instead of Konva's PNG default.
      const pixelRatio =
        imageMeasures.width > 0 && canvasMeasures.width > 0
          ? imageMeasures.width / canvasMeasures.width
          : Math.max(window.devicePixelRatio || 1, 2);

      const dataURL = getCompositeDataURL(exportOptions.mimeType, exportOptions.quality, pixelRatio);
      fileDownload(dataURL, resolvedFileName);
    },

    triggerClearMethod: () => {
      setAnnotations([]);
      setAllData(prev => ({
        ...prev,
        [images[selectedImage].id]: [],
      }));
    },

    // triggerDamageMethod: (value: string) => {
    //   if (selectedId === '') return;

    //   const updated = annotations.map((ann) =>
    //     ann.id === selectedId ? { ...ann, type: value } : ann
    // );
    //   setSelectedDamageType(value);
    // },
    triggerDamageMethod: value => {
      setSelectedDamageType(value);

      if (!selectedId) {
        console.log('Please select an annotation first.');
        return;
      }

      setAnnotations(previousAnnotations =>
        previousAnnotations.map(annotation => {
          if (annotation.id !== selectedId) {
            return annotation;
          }

          const currentDamageCode = annotation.type.substring(0, 5).trim();
          const nextType =
            currentDamageCode !== value.substring(0, 5).trim() ? `${value}-M` : `${value}${annotation.type.substring(5, 7).trim()}`;

          // A type-only edit never moves the box, but the annotation may still be holding
          // real-image-pixel coordinates (as hydrated from server metadata, never converted
          // since it was last dragged/resized). Normalize to canvas space here so that
          // save-time conversion (convertAnnotationsToImageCoordinates) doesn't scale
          // already-real-pixel coordinates up a second time and fling the box off-frame.
          const canvasScaled = scaleAnnotationForCanvas(annotation, canvasMeasures, imageMeasures);

          return {
            ...canvasScaled,
            action: currentDamageCode !== value.substring(0, 5).trim() ? 'MODIFIED' : annotation.action,
            frameWidth: canvasMeasures.width,
            frameHeight: canvasMeasures.height,
            type: nextType,
          };
        })
      );
    },
  }));

  /* ===========================
       EVENTS
    =========================== */

  const handleMouseDown = (e: any) => {
    // Let the parent decide whether an existing selection blocks a new draw
    // (Defect Images enforces single; Vehicle Images allows multiple).
    if (disableAnnotation || !isAddAnnotation || imageLoadError || newAnnotation.length > 0) {
      return;
    }

    if (!isCanvasBackgroundTarget(e.target)) {
      return;
    }

    const pos = e.target.getStage().getPointerPosition();
    if (!pos) return;

    const startCreate = () => {
      const id = uuid();

      setNewAnnotation([
        {
          id: 'new_' + id,
          x: pos.x,
          y: pos.y,
          x1: pos.x,
          y1: pos.y,
          x2: pos.x,
          y2: pos.y,
          width: 0,
          height: 0,
          rotation: 0,
          type: '00000',
          deleted: 0,
          action: 'CREATE',
          frameWidth: canvasMeasures.width,
          frameHeight: canvasMeasures.height,
          imageWidth: imageMeasures.width,
          imageHeight: imageMeasures.height,
          coordinateSpace: 'canvas',
        },
      ]);
    };

    if (onStartAnnotationCreate) {
      onStartAnnotationCreate(startCreate);
      return;
    }

    if (selectedId !== '') {
      return;
    }

    startCreate();
  };

  // creating new annotation
  const handleMouseMove = (event: any) => {
    if (disableAnnotation) event.target.getStage().container().style.cursor = 'default';
    else if (isAddAnnotation) event.target.getStage().container().style.cursor = selectedId ? 'move' : 'crosshair';
    else event.target.getStage().container().style.cursor = 'default';
    if (newAnnotation.length === 1) {
      const sx = newAnnotation[0].x;
      const sy = newAnnotation[0].y;
      const { x, y } = event.target.getStage().getPointerPosition();
      const id = uuid();
      setNewAnnotation([
        {
          id: 'new_' + id,
          x: sx,
          y: sy,
          width: x - sx,
          height: y - sy,
          rotation: 0,
          x1: sx,
          y1: sy,
          x2: x,
          y2: y,
          type: '00000',
          deleted: 0,
          action: 'CREATE',
          review_status: 'PENDING',
          frameWidth: canvasMeasures.width,
          frameHeight: canvasMeasures.height,
          imageWidth: imageMeasures.width,
          imageHeight: imageMeasures.height,
          created_by: user?.USER_ID,
          coordinateSpace: 'canvas',
        },
      ]);
    }
  };

  const clickAnnotation = (id: string) => {
    if (!id || !canInteractWithAnnotations) return;

    selectAnnotation(id);
  };
  // saving new Annotation in the annotations array
  const handleMouseUp = (event: any) => {
    if (newAnnotation.length === 1) {
      const createdAnnotation = newAnnotation[0];
      // A click without a drag produces a zero-width box (x1 === x2); discard
      // just that one instead of re-filtering every existing annotation (which
      // could silently drop an unrelated saved annotation that happens to
      // land on x1 === x2 after a scaling round-trip).
      const wasAnnotationDrawn = createdAnnotation.x1 !== createdAnnotation.x2;
      const nextAnnotations = wasAnnotationDrawn ? [...annotations, createdAnnotation] : annotations;

      setAnnotations(nextAnnotations);
      setNewAnnotation([]);

      if (wasAnnotationDrawn && createdAnnotation?.id !== '') {
        selectAnnotation(createdAnnotation.id);
      }
      event.target.getStage().container().style.cursor = disableAnnotation ? 'default' : wasAnnotationDrawn ? 'move' : 'crosshair';
    }
  };

  const removeAnnotation = (id: string) => {
    setAnnotations(annotations.map(ann => (ann.id === id ? { ...ann, deleted: 1 } : ann)));
  };

  const annotationsToDraw = useMemo(
    () => [...annotations, ...newAnnotation].map(annotation => scaleAnnotationForCanvas(annotation, canvasMeasures, imageMeasures)),
    [annotations, canvasMeasures, imageMeasures, newAnnotation]
  );

  useLayoutEffect(() => {
    if (!imageMeasures.width || !imageMeasures.height || !imageFrame?.width || !imageFrame?.height) {
      return;
    }

    const fitScale = Math.min(imageFrame.width / imageMeasures.width, imageFrame.height / imageMeasures.height, 1);
    const nextCanvas = {
      width: Math.max(1, Math.round(imageMeasures.width * fitScale)),
      height: Math.max(1, Math.round(imageMeasures.height * fitScale)),
    };

    setCanvasMeasures(previous =>
      previous.width === nextCanvas.width && previous.height === nextCanvas.height ? previous : nextCanvas
    );
    setNativeZoom({ enabled: true, fitScale });
  }, [imageFrame?.height, imageFrame?.width, imageMeasures.height, imageMeasures.width, setNativeZoom]);

  const stageWidth = canvasMeasures.width;
  const stageHeight = canvasMeasures.height;

  /* ===========================
       RENDER
    =========================== */

  return (
    <div
      className="dashboard"
      id={images[0].url}
      style={stageWidth && stageHeight ? { width: stageWidth, height: stageHeight } : undefined}>
      <div
        id="image-container"
        style={
          stageWidth && stageHeight
            ? { position: 'relative', width: stageWidth, height: stageHeight }
            : { position: 'relative' }
        }>
        <ImageContainer
          imageUrl={images[0].url}
          imageRef={imageRef}
          setImageMeasures={setImageMeasures}
          canvasMeasures={canvasMeasures}
          onLoadError={setImageLoadError}
        />
        {/* @ts-ignore */}
        <Stage
          width={stageWidth}
          height={stageHeight}
          pixelRatio={1}
          ref={stageRef}
          style={{ position: 'absolute', top: 0, left: 0 }}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}>
          <Layer>
            <Rect
              x={0}
              y={0}
              width={stageWidth}
              height={stageHeight}
              fill="rgba(0,0,0,0)"
              onMouseDown={() => {
                if (!canInteractWithAnnotations) {
                  return;
                }
                selectAnnotation('');
              }}
            />

            {annotationsToDraw.map(
              (annotation, i) =>
                annotation.deleted === 0 ? (
                  <Annotation
                    key={i}
                    shapeProps={annotation}
                    isSelected={canInteractWithAnnotations && annotation?.id === selectedId}
                    isEditable={canInteractWithAnnotations}
                    user={user}
                    removeAnnotation={removeAnnotation}
                    onSelect={() => clickAnnotation(annotation.id)}
                    onChange={(newAttrs: AnnotationType) => {
                      if (!canInteractWithAnnotations) {
                        return;
                      }
                      const rects = [...annotations];
                      rects[i] = newAttrs;
                      setAnnotations(rects);
                    }}
                  />
                ) : null
            )}
          </Layer>
        </Stage>
      </div>
    </div>
  );
});
